import { useState, useEffect } from 'react';
import { useDevBoard } from '@snipit/core/hooks/useDevBoard';
import { useAuth } from '@snipit/core/context/AuthContext';
import { MessageSquare, Send, CheckCircle, Lightbulb, Trophy, Mail, Search, Paperclip, X, FileText, Image as ImageIcon, ZoomIn, Pencil, Trash2 } from 'lucide-react';
import OptimizedImage from '@snipit/ui/components/OptimizedImage';

export default function DevBoardView() {
  const { messages, loading, leaderboard, leaderboardLoading, postMessage, subscribeToResponses, postResponse, verifyResponse, editMessage, deleteMessage } = useDevBoard();
  const { currentUser, userLicense } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editIsGuide, setEditIsGuide] = useState(false);
  const [isGuide, setIsGuide] = useState(false);
  const [activeTab, setActiveTab] = useState('qa');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [postTopic, setPostTopic] = useState('General');
  const [selectedModel, setSelectedModel] = useState('All Models');
  const [postModel, setPostModel] = useState('None');
  const [attachment, setAttachment] = useState(null);

  const TOPICS = ['All Topics', 'General', 'JavaScript', 'Python', 'React', 'HTML/CSS', 'SQL'];
  const POST_TOPICS = TOPICS.filter(t => t !== 'All Topics');

  const MODELS = ['All Models', 'None', 'ChatGPT', 'Gemini', 'Claude', 'GitHub Copilot'];
  const POST_MODELS = MODELS.filter(m => m !== 'All Models');

  if (userLicense && userLicense.tier === 'free') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-6">
        <Trophy className="w-20 h-20 text-indigo-500" />
        <h2 className="text-3xl font-bold text-white">Unlock the Developer Board</h2>
        <p className="text-gray-400 max-w-lg text-lg">
          The Developer Board is an exclusive community where you can ask coding questions, share insights, and earn DevIT Creds. Upgrade your license to join the conversation!
        </p>
        <button className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors shadow-lg shadow-indigo-500/20">
          Upgrade to Premium
        </button>
      </div>
    );
  }

  const handlePost = async () => {
    if (!newMessage.trim() && !attachment) return;
    try {
      await postMessage(newMessage, isGuide, postTopic, postModel, attachment);
      setNewMessage('');
      setAttachment(null);
      setIsGuide(false);
    } catch (e) {
      alert("Failed to post message.");
    }
  };

  const handleSaveEdit = async (msgId) => {
    if (!editContent.trim()) return;
    try {
      const originalMsg = messages.find(m => m.id === msgId);
      const updates = { content: editContent, isGuide: editIsGuide };

      // If the post is becoming a guide, or was previously a guide, we must reset its validation and lock status.
      if (editIsGuide || originalMsg?.isGuide) {
        updates.isValidatedSolution = false;
        updates.isLocked = false;
      }

      await editMessage(msgId, updates);
      setEditingMessageId(null);
      setEditContent('');
      setEditIsGuide(false);
    } catch (e) {
      console.error(e);
      alert("Failed to edit message.");
    }
  };

  const handleDelete = async (msgId) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      try {
        await deleteMessage(msgId);
      } catch (e) {
        console.error(e);
        alert("Failed to delete message.");
      }
    }
  };

  const acceptString = activeTab === 'tips' || isGuide ? '.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'image/*';

  const filteredMessages = messages.filter(msg => {
    // Filter by tab
    if (activeTab === 'qa' && msg.isGuide) return false;
    if (activeTab === 'tips') {
      if (!msg.isGuide) return false;
      // Hide unvalidated guides from the tips and tricks board until validated
      if (!msg.isValidatedSolution) return false;
    }

    // Filter by search query
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      const matchesContent = msg.content?.toLowerCase().includes(lowerQuery);
      const matchesAuthor = msg.authorName?.toLowerCase().includes(lowerQuery);
      if (!matchesContent && !matchesAuthor) return false;
    }

    // Filter by topic
    if (selectedTopic !== 'All Topics' && msg.topic !== selectedTopic) return false;

    // Filter by AI model
    if (selectedModel !== 'All Models' && msg.aiModel !== selectedModel) return false;

    return true;
  });

  const MessageThread = ({ msg, currentUser, subscribeToResponses, postResponse, verifyResponse }) => {
    const [responses, setResponses] = useState([]);
    const [newResponse, setNewResponse] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const unsubscribe = subscribeToResponses(msg.id, (resData) => {
        setResponses(resData);
        setLoading(false);
      });
      return () => unsubscribe();
    }, [msg.id, subscribeToResponses]);

    const handleReply = async () => {
      if (!newResponse.trim()) return;
      try {
        await postResponse(msg.id, newResponse);
        setNewResponse('');
      } catch (e) {
        console.error(e);
        alert("Failed to post response");
      }
    };

    return (
      <div className="mt-4 pl-14 space-y-3 w-full">
        {/* Responses */}
        {responses.map(res => (
          <div key={res.id} className={`bg-[#1a1a1a] p-3 rounded-lg border ${res.isVerified ? 'border-emerald-500/50' : 'border-gray-800'}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold text-white text-xs">{res.authorName}</span>
              <span className="text-[10px] text-gray-500">{new Date(res.createdAt).toLocaleString()}</span>
              {res.isVerified && <span className="text-[10px] text-emerald-400 font-bold ml-auto flex items-center gap-1"><CheckCircle size={10} /> Verified Solution</span>}
            </div>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{res.content}</p>

            {/* Mark as Verified Button for OP */}
            {!msg.isLocked && currentUser?.uid === msg.userId && currentUser?.uid !== res.userId && !res.isVerified && (
              <div className="mt-2">
                <button
                  onClick={() => verifyResponse(msg.id, res.id, res.userId)}
                  className="text-[10px] flex items-center gap-1 bg-emerald-900/30 text-emerald-400 hover:bg-emerald-900/50 border border-emerald-900/50 px-2 py-1 rounded transition"
                >
                  <CheckCircle size={12} /> Mark as Verified (+5 Creds to Author)
                </button>
              </div>
            )}
          </div>
        ))}
        {/* Reply Box */}
        {!msg.isLocked && (
          <div className="flex gap-2 w-full">
            <input
              type="text"
              className="grow bg-[#1a1a1a] border border-gray-800 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
              placeholder="Write a response..."
              value={newResponse}
              onChange={e => setNewResponse(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleReply()}
            />
            <button onClick={handleReply} className="bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/40 px-3 py-1.5 rounded text-sm font-medium transition-colors shrink-0">
              Reply
            </button>
          </div>
        )}
        {msg.isLocked && (
          <div className="text-xs text-gray-500 italic mt-2">
            This thread is locked because a solution was verified.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full bg-[#121212] text-gray-300 flex flex-col p-6 lg:p-8">
      <div className="flex justify-between items-center mb-6 shrink-0">
        <h1 className="text-white font-bold text-2xl flex items-center gap-2">
          <MessageSquare className="text-indigo-500" /> Dev Board
        </h1>

        {(activeTab === 'qa' || activeTab === 'tips') && (
          <div className="flex gap-3">
            <select
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
              className="bg-[#1a1a1a] border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-indigo-500 transition-colors"
            >
              {TOPICS.map(topic => (
                <option key={topic} value={topic}>{topic}</option>
              ))}
            </select>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="bg-[#1a1a1a] border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-indigo-500 transition-colors"
            >
              {MODELS.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
            <div className="relative w-64 hidden sm:block">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-gray-800 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
              />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-800 pb-2 overflow-x-auto shrink-0">
        <button
          onClick={() => setActiveTab('qa')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'qa' ? 'bg-indigo-600/20 text-indigo-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}
        >
          <MessageSquare size={16} /> Q&A
        </button>
        <button
          onClick={() => setActiveTab('tips')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'tips' ? 'bg-indigo-600/20 text-indigo-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}
        >
          <Lightbulb size={16} /> Tips & Tricks
        </button>
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'leaderboard' ? 'bg-indigo-600/20 text-indigo-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}
        >
          <Trophy size={16} /> Leaderboard
        </button>
      </div>

      <div className="flex flex-col grow overflow-hidden bg-[#1a1a1a] rounded-xl border border-gray-800 shadow-xl">
        {activeTab === 'qa' || activeTab === 'tips' ? (
          <>
            <div className="grow overflow-y-auto p-6 space-y-4 custom-scrollbar">
              {loading ? (
                <p className="text-center text-gray-500">Loading messages...</p>
              ) : filteredMessages.length === 0 ? (
                <p className="text-center text-gray-500">No messages yet in this category. Start the conversation!</p>
              ) : (
                filteredMessages.map(msg => (
                  <div key={msg.id} className="bg-[#121212] p-4 rounded-lg border border-gray-800 flex flex-col gap-4">
                    <div className="flex gap-4">
                      {msg.authorPhoto ? (
                        <OptimizedImage src={msg.authorPhoto} alt={msg.authorName} className="w-10 h-10 rounded-full shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center shrink-0">
                          <span className="font-bold text-sm">{msg.authorName?.charAt(0) || '?'}</span>
                        </div>
                      )}
                      <div className="flex flex-col grow">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-white text-sm">{msg.authorName}</span>
                          <span className="text-[10px] text-gray-500">{new Date(msg.createdAt).toLocaleString()}</span>
                          {msg.topic && <span className="bg-gray-800 text-gray-300 border border-gray-700 text-[10px] px-2 py-0.5 rounded font-medium">{msg.topic}</span>}
                          {msg.aiModel && msg.aiModel !== 'None' && <span className="bg-blue-900/40 text-blue-400 border border-blue-900/60 text-[10px] px-2 py-0.5 rounded font-medium">{msg.aiModel}</span>}
                          {msg.isGuide && <span className="bg-indigo-900/40 text-indigo-400 border border-indigo-900/60 text-[10px] px-2 py-0.5 rounded font-bold">Guide / Tip</span>}
                          {msg.isLocked && <span className="text-[10px] text-emerald-400 font-bold ml-auto flex items-center gap-1"><CheckCircle size={10} /> Resolved</span>}
                        </div>
                        {editingMessageId === msg.id ? (
                          <div className="flex flex-col gap-2 mt-2">
                            <textarea
                              className="w-full bg-[#1a1a1a] p-3 rounded-lg outline-none border border-gray-700 text-sm resize-none custom-scrollbar focus:border-indigo-500/50 transition-colors"
                              rows="3"
                              value={editContent}
                              onChange={e => setEditContent(e.target.value)}
                            />
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`editIsGuide-${msg.id}`}
                                checked={editIsGuide}
                                onChange={e => setEditIsGuide(e.target.checked)}
                                className="rounded bg-[#1a1a1a] border-gray-700"
                              />
                              <label htmlFor={`editIsGuide-${msg.id}`} className="text-xs text-gray-400">
                                Mark as Tips & Tricks Guide
                              </label>
                            </div>
                            <div className="flex gap-2 mt-1">
                              <button onClick={() => handleSaveEdit(msg.id)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors">Save</button>
                              <button onClick={() => setEditingMessageId(null)} className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-300 whitespace-pre-wrap">{msg.content}</p>
                        )}

                        {/* Render Attachment */}
                        {msg.attachmentUrl && (
                          <div className="mt-3">
                            {msg.attachmentType?.startsWith('image/') ? (
                              <a href={msg.attachmentUrl} target="_blank" rel="noopener noreferrer" className="inline-block relative group mt-1">
                                <OptimizedImage src={msg.attachmentUrl} alt="attachment" className="max-h-28 rounded border border-gray-700 group-hover:opacity-75 transition object-cover" />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none bg-black/20 rounded">
                                  <ZoomIn size={24} className="text-white drop-shadow-md" />
                                </div>
                              </a>
                            ) : (
                              <a href={msg.attachmentUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-[#1a1a1a] border border-gray-700 rounded-lg hover:bg-gray-800 transition w-fit">
                                <FileText size={24} className="text-indigo-400" />
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-white">{msg.attachmentName || 'Document Attachment'}</span>
                                  <span className="text-[10px] text-gray-500">Click to view/download</span>
                                </div>
                              </a>
                            )}
                          </div>
                        )}

                        {/* Note: Validation is now done exclusively via the Admin Console */}
                        {msg.isGuide && msg.isValidatedSolution && (
                          <div className="mt-3 flex items-center gap-1 text-[10px] text-emerald-400 font-bold">
                            <CheckCircle size={12} /> Guide Validated
                          </div>
                        )}

                        {/* Thread Toggle for Q&A */}
                        {!msg.isGuide && (
                          <div className="mt-3 flex items-center gap-4">
                            <button
                              onClick={() => setExpandedMessageId(expandedMessageId === msg.id ? null : msg.id)}
                              className="text-xs text-gray-500 hover:text-indigo-400 flex items-center gap-1 transition"
                            >
                              <MessageSquare size={12} /> {expandedMessageId === msg.id ? 'Hide Thread' : 'View Thread / Reply'}
                            </button>
                            {msg.userId === currentUser?.uid && !msg.isLocked && (
                              <>
                                <button
                                  onClick={() => { setEditingMessageId(msg.id); setEditContent(msg.content); setEditIsGuide(msg.isGuide || false); }}
                                  className="text-xs text-gray-500 hover:text-indigo-400 flex items-center gap-1 transition"
                                >
                                  <Pencil size={12} /> Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(msg.id)}
                                  className="text-xs text-gray-500 hover:text-red-400 flex items-center gap-1 transition"
                                >
                                  <Trash2 size={12} /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        {msg.isGuide && msg.userId === currentUser?.uid && (
                          <div className="mt-3 flex items-center gap-4">
                            <button
                              onClick={() => { setEditingMessageId(msg.id); setEditContent(msg.content); setEditIsGuide(msg.isGuide || false); }}
                              className="text-xs text-gray-500 hover:text-indigo-400 flex items-center gap-1 transition"
                            >
                              <Pencil size={12} /> Edit
                            </button>
                            <button
                              onClick={() => handleDelete(msg.id)}
                              className="text-xs text-gray-500 hover:text-red-400 flex items-center gap-1 transition"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Inline Thread Rendering */}
                    {expandedMessageId === msg.id && !msg.isGuide && (
                      <MessageThread
                        msg={msg}
                        currentUser={currentUser}
                        subscribeToResponses={subscribeToResponses}
                        postResponse={postResponse}
                        verifyResponse={verifyResponse}
                      />
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Input Area */}
            <div className="p-4 bg-[#121212] border-t border-gray-800 shrink-0">
              <div className="flex gap-2 mb-2 items-center">
                <input
                  type="checkbox"
                  id="isGuide"
                  checked={activeTab === 'tips' ? true : isGuide}
                  onChange={e => activeTab !== 'tips' && setIsGuide(e.target.checked)}
                  disabled={activeTab === 'tips'}
                  className="rounded bg-[#1a1a1a] border-gray-700 disabled:opacity-50"
                />
                <label htmlFor="isGuide" className="text-xs text-gray-400">
                  {activeTab === 'tips' ? 'Posting in Tips & Tricks (Eligible for DevIT Creds if validated)' : 'Post as a Guide / Tip (Eligible for DevIT Creds if validated)'}
                </label>
              </div>
              {attachment && (
                <div className="flex items-center gap-2 mb-2 p-2 bg-[#1a1a1a] border border-gray-700 rounded-lg">
                  {attachment.type.startsWith('image/') ? <ImageIcon size={16} className="text-blue-400" /> : <FileText size={16} className="text-indigo-400" />}
                  <span className="text-xs text-gray-300 truncate max-w-50">{attachment.name}</span>
                  <button onClick={() => setAttachment(null)} className="text-gray-500 hover:text-red-400 transition"><X size={14} /></button>
                </div>
              )}
              <div className="flex gap-2">
                <div className="flex flex-col gap-2 shrink-0">
                  <select
                    value={postTopic}
                    onChange={(e) => setPostTopic(e.target.value)}
                    className="bg-[#1a1a1a] border border-gray-800 rounded-lg px-3 py-1 text-sm text-gray-300 focus:outline-none focus:border-indigo-500 transition-colors h-7.5"
                  >
                    {POST_TOPICS.map(topic => (
                      <option key={topic} value={topic}>{topic}</option>
                    ))}
                  </select>
                  <select
                    value={postModel}
                    onChange={(e) => setPostModel(e.target.value)}
                    className="bg-[#1a1a1a] border border-gray-800 rounded-lg px-3 py-1 text-sm text-gray-300 focus:outline-none focus:border-indigo-500 transition-colors h-7.5"
                  >
                    {POST_MODELS.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  className="grow bg-[#1a1a1a] p-3 rounded-lg outline-none border border-gray-800 text-sm resize-none h-16 custom-scrollbar focus:border-indigo-500/50 transition-colors"
                  placeholder={activeTab === 'tips' ? "Share your best tip or guide..." : "Ask a question..."}
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (activeTab === 'tips') {
                        setIsGuide(true);
                      }
                      handlePost();
                    }
                  }}
                />
                <label className="flex items-center justify-center p-3 bg-[#1a1a1a] border border-gray-800 rounded-lg cursor-pointer hover:bg-gray-800 transition shrink-0">
                  <Paperclip size={20} className="text-gray-400" />
                  <input type="file" className="hidden" accept={acceptString} onChange={e => e.target.files[0] && setAttachment(e.target.files[0])} />
                </label>
                <button
                  onClick={() => {
                    if (activeTab === 'tips') setIsGuide(true);
                    handlePost();
                  }}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white p-3 rounded-lg font-bold transition-colors flex items-center justify-center shrink-0 w-16"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </>
        ) : activeTab === 'leaderboard' ? (
          <div className="flex flex-col items-center h-full p-6 lg:p-8 space-y-6 overflow-y-auto w-full">
            <div className="text-center space-y-2 shrink-0 mt-4">
              <Trophy className="w-12 h-12 text-yellow-500 mx-auto" />
              <h2 className="text-2xl font-bold text-white">Leaderboard</h2>
              <p className="text-gray-400 max-w-md text-sm">Top contributors earning DevIT Creds by sharing validated solutions and helpful tips.</p>
            </div>

            <div className="w-full max-w-2xl bg-[#121212] border border-gray-800 rounded-xl overflow-hidden shadow-2xl">
              {leaderboardLoading ? (
                <div className="p-8 text-center text-gray-500">Loading top contributors...</div>
              ) : leaderboard.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No users found on the leaderboard.</div>
              ) : (
                <table className="w-full text-left">
                  <thead className="bg-[#1a1a1a] text-gray-400 text-xs uppercase border-b border-gray-800">
                    <tr>
                      <th className="px-6 py-4 font-semibold">Rank</th>
                      <th className="px-6 py-4 font-semibold">Developer</th>
                      <th className="px-6 py-4 font-semibold text-right">DevIT Creds</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {leaderboard.map((user, index) => (
                      <tr key={user.id} className="hover:bg-[#1a1a1a]/50 transition-colors">
                        <td className="px-6 py-4">
                          {index === 0 ? (
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500/20 text-yellow-500 font-bold border border-yellow-500/50">1</span>
                          ) : index === 1 ? (
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-400/20 text-gray-400 font-bold border border-gray-400/50">2</span>
                          ) : index === 2 ? (
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-700/20 text-amber-600 font-bold border border-amber-700/50">3</span>
                          ) : (
                            <span className="flex items-center justify-center w-8 h-8 text-gray-500 font-bold">{index + 1}</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {user.photoURL ? (
                              <OptimizedImage src={user.photoURL} alt={user.displayName} className="w-10 h-10 rounded-full border border-gray-700" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-indigo-900/40 text-indigo-400 border border-indigo-900/60 flex items-center justify-center font-bold">
                                {user.displayName?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                            <div className="flex flex-col">
                              <span className="font-bold text-white text-sm">{user.displayName}</span>
                              <span className="text-xs text-gray-500">{user.role === 'admin' ? 'Admin' : 'Developer'}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="inline-flex items-center gap-1 bg-emerald-900/20 text-emerald-400 border border-emerald-900/40 px-3 py-1 rounded-full font-bold text-sm">
                            {user.devItCreds || 0}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
